import React from 'react';
import { motion } from 'framer-motion';

// Base skeleton component with shimmer effect
const SkeletonBase: React.FC<{
  className?: string;
  width?: string;
  height?: string;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
}> = ({ className = '', width = 'w-full', height = 'h-4', rounded = 'md' }) => {
  const roundedClasses = {
    none: '',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full',
  };

  return (
    <motion.div
      className={`bg-gray-200 ${width} ${height} ${roundedClasses[rounded]} ${className}`}
      animate={{
        background: [
          'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
        ],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );
};

// Product card skeleton
export const ProductCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg shadow-md p-4 space-y-3">
    <SkeletonBase height="h-32" rounded="lg" />
    <div className="space-y-2">
      <SkeletonBase height="h-5" width="w-3/4" />
      <SkeletonBase height="h-4" width="w-1/2" />
      <SkeletonBase height="h-6" width="w-1/3" />
    </div>
    <div className="flex justify-between items-center pt-2">
      <SkeletonBase height="h-8" width="w-16" rounded="lg" />
      <SkeletonBase height="h-8" width="w-20" rounded="lg" />
    </div>
  </div>
);

// Table row skeleton
export const TableRowSkeleton: React.FC<{ columns?: number }> = ({ columns = 4 }) => (
  <div className="flex items-center space-x-4 p-4 border-b border-gray-200">
    {Array.from({ length: columns }).map((_, index) => (
      <SkeletonBase
        key={index}
        height="h-4"
        width={index === 0 ? 'w-20' : index === columns - 1 ? 'w-24' : 'w-32'}
      />
    ))}
  </div>
);

// Table skeleton with header
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4,
}) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    {/* Header */}
    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
      <div className="flex items-center space-x-4">
        {Array.from({ length: columns }).map((_, index) => (
          <SkeletonBase
            key={index}
            height="h-5"
            width={index === 0 ? 'w-20' : index === columns - 1 ? 'w-24' : 'w-32'}
          />
        ))}
      </div>
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, index) => (
      <TableRowSkeleton key={index} columns={columns} />
    ))}
  </div>
);

// Form skeleton
export const FormSkeleton: React.FC<{ fields?: number }> = ({ fields = 4 }) => (
  <div className="space-y-6">
    {Array.from({ length: fields }).map((_, index) => (
      <div key={index} className="space-y-2">
        <SkeletonBase height="h-4" width="w-24" />
        <SkeletonBase height="h-10" rounded="md" />
      </div>
    ))}
    <div className="flex justify-end space-x-3 pt-4">
      <SkeletonBase height="h-10" width="w-20" rounded="lg" />
      <SkeletonBase height="h-10" width="w-24" rounded="lg" />
    </div>
  </div>
);

// Card skeleton
export const CardSkeleton: React.FC<{ lines?: number }> = ({ lines = 3 }) => (
  <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
    <SkeletonBase height="h-6" width="w-3/4" />
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, index) => (
        <SkeletonBase
          key={index}
          height="h-4"
          width={index === lines - 1 ? 'w-1/2' : 'w-full'}
        />
      ))}
    </div>
  </div>
);

// List skeleton
export const ListSkeleton: React.FC<{ items?: number }> = ({ items = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: items }).map((_, index) => (
      <div key={index} className="flex items-center space-x-3 p-3 bg-white rounded-lg shadow-sm">
        <SkeletonBase height="h-10" width="w-10" rounded="full" />
        <div className="flex-1 space-y-2">
          <SkeletonBase height="h-4" width="w-3/4" />
          <SkeletonBase height="h-3" width="w-1/2" />
        </div>
        <SkeletonBase height="h-6" width="w-16" rounded="lg" />
      </div>
    ))}
  </div>
);

// Grid skeleton
export const GridSkeleton: React.FC<{ items?: number; columns?: number }> = ({
  items = 6,
  columns = 3,
}) => (
  <div
    className="grid gap-4"
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
  >
    {Array.from({ length: items }).map((_, index) => (
      <ProductCardSkeleton key={index} />
    ))}
  </div>
);

// Page skeleton with header
export const PageSkeleton: React.FC<{ showHeader?: boolean }> = ({ showHeader = true }) => (
  <div className="space-y-6">
    {showHeader && (
      <div className="flex justify-between items-center">
        <SkeletonBase height="h-8" width="w-48" />
        <SkeletonBase height="h-10" width="w-32" rounded="lg" />
      </div>
    )}
    <div className="space-y-4">
      <SkeletonBase height="h-4" width="w-2/3" />
      <SkeletonBase height="h-4" width="w-1/2" />
    </div>
  </div>
);

// Modal skeleton
export const ModalSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-auto">
    <div className="space-y-4">
      <SkeletonBase height="h-6" width="w-3/4" />
      <SkeletonBase height="h-4" width="w-full" />
      <SkeletonBase height="h-4" width="w-2/3" />
      <div className="flex justify-end space-x-3 pt-4">
        <SkeletonBase height="h-9" width="w-20" rounded="lg" />
        <SkeletonBase height="h-9" width="w-24" rounded="lg" />
      </div>
    </div>
  </div>
);

// Stats skeleton
export const StatsSkeleton: React.FC<{ cards?: number }> = ({ cards = 4 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    {Array.from({ length: cards }).map((_, index) => (
      <div key={index} className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <SkeletonBase height="h-4" width="w-20" />
            <SkeletonBase height="h-8" width="w-16" />
          </div>
          <SkeletonBase height="h-12" width="w-12" rounded="lg" />
        </div>
      </div>
    ))}
  </div>
);

// Chart skeleton
export const ChartSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="space-y-4">
      <SkeletonBase height="h-6" width="w-1/3" />
      <SkeletonBase height="h-64" rounded="lg" />
      <div className="flex justify-center space-x-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="flex items-center space-x-2">
            <SkeletonBase height="h-3" width="w-3" rounded="full" />
            <SkeletonBase height="h-4" width="w-16" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default SkeletonBase; 